import { useCallback, useEffect, useRef, useState } from "react";
import type { MaterialListItem } from "@edu/shared";
import { ApiError } from "../../lib/api.ts";
import { listErrorText } from "../../lib/list-error.ts";
import { deleteMaterial, listMaterials, revokeMaterial } from "./student-materials-api.ts";

/**
 * Ссылки и файлы, отправленные ученику: своя загрузка, своя догрузка, свой отказ.
 *
 * ⚠️ **Отдельным запросом, а не полем карточки ученика**, по той же причине, что
 * «Где дыры» (`use-student-skills.ts`): панель, уронившая карточку, забрала бы с
 * собой занятия, оплаты и ссылку. Плюс ручка списка уже есть и постраничная
 * (`GET /v1/materials?studentId=`), а второй путь к тем же строкам через
 * карточку разошёлся бы с ней при первой правке.
 *
 * Догрузка кнопкой, а не прокруткой: разбор у `useHomeworkList`.
 *
 * ⚠️ **Отзыв и удаление правят список на месте, без перечитывания.** Сервер
 * отвечает на них `204` без тела, а исход известен заранее: отозванный остаётся
 * у преподавателя с датой отзыва (ученику он больше не виден), удалённый
 * пропадает. Перечитывание сбросило бы догруженные страницы к первой.
 * `material_not_found` в ответ на любое из двух значит «его уже нет» (удалили в
 * соседней вкладке), и строка уходит так же, без отказа на экране.
 */
export interface StudentMaterialsState {
  /** `null` до первого ответа: панель показывает ожидание, а не пустоту. */
  items: readonly MaterialListItem[] | null;
  nextCursor: string | null;
  busy: boolean;
  err: string | null;
  loadMore: () => void;
  /** Перечитать первую страницу: после отправки нового материала. */
  reload: () => void;
  revoke: (materialId: string) => void;
  remove: (materialId: string) => void;
  /** Строка, над которой сейчас летит отзыв или удаление. */
  acting: string | null;
}

export function useStudentMaterials(studentUserId: string): StudentMaterialsState {
  const [items, setItems] = useState<MaterialListItem[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  /**
   * Поколение списка. Ответ догрузки, запрошенной до перечитывания, обязан не
   * приклеиться к новому списку: его курсор снят с другого чтения.
   */
  const generation = useRef(0);

  const reload = useCallback(() => {
    if (studentUserId === "") return;
    generation.current += 1;
    const mine = generation.current;
    setBusy(true);
    setErr(null);
    listMaterials(studentUserId, null)
      .then((page) => {
        if (generation.current !== mine) return;
        setItems(page.items);
        setCursor(page.nextCursor);
      })
      .catch((e: unknown) => {
        // Прошлый список не стираем: перечитывание, упавшее по сети, не обязано
        // уносить с экрана то, что уже прочитано
        if (generation.current === mine) setErr(loadError(e));
      })
      .finally(() => {
        if (generation.current === mine) setBusy(false);
      });
  }, [studentUserId]);

  useEffect(() => {
    // Другой ученик это другой список: прежние строки не показываем ни кадра
    setItems(null);
    setCursor(null);
    reload();
  }, [reload]);

  const loadMore = (): void => {
    if (cursor === null || busy) return;
    const mine = generation.current;
    const from = cursor;
    setBusy(true);
    listMaterials(studentUserId, from)
      .then((page) => {
        if (generation.current !== mine) return;
        setItems((prev) => [...(prev ?? []), ...page.items]);
        setCursor(page.nextCursor);
        setErr(null);
      })
      .catch((e: unknown) => {
        if (generation.current === mine) setErr(loadError(e));
      })
      .finally(() => {
        if (generation.current === mine) setBusy(false);
      });
  };

  const act = (
    materialId: string,
    run: (id: string) => Promise<void>,
    apply: (list: MaterialListItem[]) => MaterialListItem[],
  ): void => {
    if (acting !== null) return;
    setActing(materialId);
    setErr(null);
    const drop = (list: MaterialListItem[]) => list.filter((m) => m.id !== materialId);
    run(materialId)
      .then(() => setItems((prev) => (prev === null ? prev : apply(prev))))
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.code === "material_not_found") {
          setItems((prev) => (prev === null ? prev : drop(prev)));
          return;
        }
        setErr(
          !(e instanceof ApiError)
            ? "Нет связи. Проверьте интернет и попробуйте ещё раз."
            : e.code === "view_only"
              ? e.message
              : "Не получилось. Обновите страницу и попробуйте ещё раз.",
        );
      })
      .finally(() => setActing(null));
  };

  const revoke = (materialId: string): void => {
    const at = Date.now();
    act(materialId, revokeMaterial, (list) =>
      list.map((m) => (m.id === materialId ? { ...m, revokedAt: at } : m)),
    );
  };

  const remove = (materialId: string): void => {
    act(materialId, deleteMaterial, (list) => list.filter((m) => m.id !== materialId));
  };

  return { items, nextCursor: cursor, busy, err, loadMore, reload, revoke, remove, acting };
}

/**
 * Отказ загрузки списка словами. Без своего текста `listErrorText` показал бы
 * сообщение отказа как есть, то есть «HTTP 429: rate_limited»; тот же приём, что
 * у списка домашки (`use-homework-list.ts`).
 */
function loadError(e: unknown): string {
  return listErrorText(e, "Не вышло получить список. Попробуйте ещё раз.");
}
